import { Col, Form, Row } from "react-bootstrap";
import { formatEwcDisplayCode } from "../../../components/Forms/EwcCodeInput";
import type { IRegDataWarning } from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { HazardousMismatchAlert } from "./ReconciliationMismatchAlerts";

export interface EwcRegDataComparisonRowProps {
    mode: "display" | "edit";
    editedValue?: Record<string, unknown>;
    warnings?: IRegDataWarning[];
    onChange?: (editedValue: Record<string, unknown>) => void;
    displayCode?: string;
    displayDescription?: string;
    isHazardous?: boolean;
    /** When true, show "Hazardous? —" (source has no asterisk-marked codes). */
    hazardIndeterminate?: boolean;
    /** When "below", description renders under the code in muted text (System Data). */
    descriptionPlacement?: "inline" | "below";
}

export function EwcRegDataComparisonRow({
    mode,
    editedValue,
    warnings = [],
    onChange,
    displayCode,
    displayDescription,
    isHazardous,
    hazardIndeterminate = false,
    descriptionPlacement = "inline",
}: EwcRegDataComparisonRowProps) {
    if (mode === "display") {
        const formattedCode = formatEwcDisplayCode(displayCode);
        const hazardousLabel = hazardIndeterminate ? (
            <span className="ms-2 text-muted">| Hazardous? —</span>
        ) : (
            <span className="ms-2 text-muted">| Hazardous? {isHazardous ? "Yes" : "No"}</span>
        );

        if (descriptionPlacement === "below") {
            return (
                <div className="small">
                    <div>
                        <span>{formattedCode}</span>
                        {hazardousLabel}
                    </div>
                    {displayDescription ? <div className="text-muted">{displayDescription}</div> : null}
                </div>
            );
        }

        return (
            <div className="small">
                <span>{formattedCode}</span>
                {displayDescription ? <span> — {displayDescription}</span> : null}
                {hazardousLabel}
            </div>
        );
    }

    const code = String(editedValue?.code ?? "");
    const description = String(editedValue?.description ?? "");
    const hazardous = Boolean(editedValue?.isHazardous);

    return (
        <Row className="g-2 small">
            <Col md={3}>
                <Form.Group>
                    <Form.Label className="mb-0">Code</Form.Label>
                    <Form.Control
                        size="sm"
                        value={code}
                        onChange={(e) => onChange?.({ ...editedValue, code: e.target.value })}
                    />
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group>
                    <Form.Label className="mb-0">Description</Form.Label>
                    <Form.Control
                        size="sm"
                        value={description}
                        onChange={(e) => onChange?.({ ...editedValue, description: e.target.value })}
                    />
                </Form.Group>
            </Col>
            <Col md={3}>
                <Form.Group>
                    <Form.Label className="mb-0">Hazardous?</Form.Label>
                    <Form.Check
                        type="checkbox"
                        checked={hazardous}
                        onChange={(e) => onChange?.({ ...editedValue, isHazardous: e.target.checked })}
                        label={hazardous ? "Yes" : "No"}
                    />
                </Form.Group>
            </Col>
            <HazardousMismatchAlert warnings={warnings} />
        </Row>
    );
}
